/**
 * 文案 key 的中文描述。Admin 全中文，运营按描述理解每个 key 的用途，不直接看英文 key。
 *
 * 文案体系（重要）：
 *   - global_text：全局基底库，所有 bot 共享。默认值由 packages/db seeds.ts 三语 seed，
 *     migrate 时自动 upsert（ON CONFLICT DO NOTHING，运营改过的不被覆盖）。
 *     Admin 路径：/texts（GlobalTextsPage）。
 *   - bot_text：per-bot 覆盖层。仅在某 bot 文案与全局值不同时才写一行。
 *     Admin 路径：BotDetail → 文案 tab。
 *
 * 解析：TextRepo.resolveTexts 对每个 key 沿 fallback chain（如 zh-CN -> zh -> default）
 * 优先 bot_text 后 global_text，最终 default 桶兜底。
 *
 * 加新 key：写默认值到 seeds.ts 三语 + 此处中文描述 -> migrate 自动 seed 进 global_text。
 * 未收录的 key 由 textKeyZh 回退显示 key 本身。
 */
export const TEXT_KEY_ZH: Record<string, string> = {
  welcome: "欢迎语（/start 命令回复）",
  "start.menu_hint": "开始 · 菜单按钮提示",

  "balance.label": "次数余额 · 单位标签",
  "empty.hint": "转盘 · 次数用完提示",

  "title.eyebrow": "首页 · 小标题",
  "title.sub": "首页 · 副标题",

  "wheel.center.spin": "转盘中心按钮 · 抽奖文案",
  "wheel.center.empty": "转盘中心按钮 · 无次数文案",

  "spin.spinning": "抽奖 · 转动中文案",
  "spin.success": "抽奖 · 中奖成功文案",
  "spin.thanks": "抽奖 · 谢谢参与文案",
  "spin.error.insufficient": "抽奖错误 · 次数不足",
  "spin.error.no_prize_available": "抽奖错误 · 无可用奖品",
  "spin.error.rate_limited": "抽奖错误 · 操作过于频繁",
  "spin.error.unauthorized": "抽奖错误 · 登录过期",

  "spins.balance": "次数 · 余额文案（命令回复）",
  "spins.empty": "次数 · 余额为空文案",
  "spins.play_hint": "次数 · 去玩提示",
  "spins.refresh.button": "次数 · 拉取最新按钮",
  "spins.refresh.no_change": "次数 · 无新存款提示",
  "spins.refresh.success": "次数 · 拉取成功提示",

  "modal.you_won": "中奖弹窗 · 恭喜中奖标题",
  "modal.jackpot_headline": "中奖弹窗 · 头奖标题",
  "modal.so_close": "中奖弹窗 · 差一点文案",
  "modal.spins_unit": "中奖弹窗 · 次数单位",
  "modal.continue": "中奖弹窗 · 继续按钮",
  "modal.try_again": "中奖弹窗 · 再来一次按钮",
  "modal.back_to_wheel": "中奖弹窗 · 返回转盘按钮",
  "modal.claim_later": "中奖弹窗 · 稍后领取按钮",

  "nav.earn_more": "底部导航 · 获取次数",
  "nav.help": "底部导航 · 帮助",
  "nav.history": "底部导航 · 战绩",
  "nav.leaderboard": "底部导航 · 排行榜",

  "earn.title": "获取次数 · 页面标题",
  "earn.subtitle": "获取次数 · 副标题",
  "earn.daily.label": "获取次数 · 每日签到标题",
  "earn.daily.value": "获取次数 · 每日签到说明",
  "earn.refer.label": "获取次数 · 邀请好友标题",
  "earn.refer.value": "获取次数 · 邀请好友说明",
  "earn.task.label": "获取次数 · 完成任务标题",
  "earn.task.value": "获取次数 · 完成任务说明",
  "earn.topup.label": "获取次数 · 充值标题",
  "earn.topup.value": "获取次数 · 充值说明",

  "help.eyebrow": "帮助 · 小标题（玩法说明）",
  "help.body": "帮助 · 正文（命令回复）",
  "help.empty": "帮助 · 空状态说明",
  "help.support_label": "帮助 · 联系客服按钮",
  "support.body": "客服 · 正文（/support 命令回复）",

  "history.empty": "战绩 · 空状态文案",
  "history.empty_sub": "战绩 · 空状态副文案",
  "history.item": "战绩 · 单条记录格式",
  "history.label.needs_shipping": "战绩 · 待填地址标记",
  "history.claim.button": "战绩 · 领奖按钮",
  "history.claim.claiming": "战绩 · 派彩已发起提示",
  "history.claim.processing": "战绩 · 派彩中状态",
  "history.claim.paid": "战绩 · 已到账状态",
  "history.claim.failed": "战绩 · 派彩失败提示",
  "history.claim.need_binding": "战绩 · 需先绑定提示",

  "leaderboard.empty": "排行榜 · 空状态文案",
  "leaderboard.empty_sub": "排行榜 · 空状态副文案",
  "leaderboard.you": "排行榜 · 「你」标记",
  "leaderboard.your_rank": "排行榜 · 你的排名文案",

  "language.label": "语言 · 标签",
  "language.auto": "语言选项 · 自动",
  "language.zh": "语言选项 · 中文",
  "language.hi": "语言选项 · 印地语",
  "lang.confirmed": "语言 · 切换成功提示",

  "binding.title": "绑定 · 页面标题",
  "binding.placeholder": "绑定 · 会员 ID 输入框占位",
  "binding.success": "绑定 · 成功提示",
  "binding.button.lookup": "绑定 · 查询按钮",
  "binding.button.confirm": "绑定 · 确认按钮",
  "binding.button.back": "绑定 · 重新输入按钮",
  "binding.button.register": "绑定 · 去注册按钮",
  "binding.error.not_found": "绑定 · 会员未找到错误",
  "binding.error.already_bound": "绑定 · 已绑定其他账号错误",
  "binding.preview.title": "绑定预览 · 标题",
  "binding.preview.intro": "绑定预览 · 确认提示语",
  "binding.preview.name": "绑定预览 · 姓名字段",
  "binding.preview.account": "绑定预览 · 账号字段",
  "binding.preview.balance": "绑定预览 · 余额字段",

  "invite.title": "邀请 · 页面标题",
  "invite.intro": "邀请 · 引导说明",
  "invite.link_label": "邀请 · 邀请链接标签",
  "invite.copy": "邀请 · 复制链接按钮",
  "invite.copied": "邀请 · 已复制提示",
  "invite.share": "邀请 · 分享按钮",
  "invite.share_text": "邀请 · 分享文案",
  "invite.stat.friends": "邀请 · 已加入好友统计",
  "invite.stat.spins": "邀请 · 已获得次数统计",
  "invite.cmd.wheel": "邀请命令 · 转盘深链文案",
  "invite.cmd.wg": "邀请命令 · 拉新注册链接文案",
  "invite.cmd.bind_hint": "邀请命令 · 未绑定提示",
  "invite.claim.section_title": "邀请申报 · 区块标题",
  "invite.claim.list_title": "邀请申报 · 我的申报列表标题",
  "invite.claim.intro": "邀请申报 · 引导说明",
  "invite.claim.link_label": "邀请申报 · 注册邀请链接标签",
  "invite.claim.placeholder": "邀请申报 · 会员 ID 输入框占位",
  "invite.claim.submit": "邀请申报 · 提交按钮",
  "invite.claim.refresh": "邀请申报 · 刷新状态按钮",
  "invite.claim.empty": "邀请申报 · 空状态",
  "invite.claim.result.granted": "邀请申报 · 验证成功提示",
  "invite.claim.result.pending": "邀请申报 · 申报成功待首存提示",
  "invite.claim.status.granted": "邀请申报 · 已发奖励状态",
  "invite.claim.status.pending_deposit": "邀请申报 · 待首存状态",
  "invite.claim.error.self": "邀请申报 · 不能申报自己错误",
  "invite.claim.error.not_invitee": "邀请申报 · 非你下级错误",
  "invite.claim.error.already_claimed": "邀请申报 · 已被申报错误",

  "shipping.title": "收件 · 页面标题",
  "shipping.intro": "收件 · 引导说明",
  "shipping.modal.intro": "收件弹窗 · 引导说明",
  "shipping.label.recipient": "收件字段 · 收件人",
  "shipping.label.phone": "收件字段 · 手机",
  "shipping.label.country": "收件字段 · 国家",
  "shipping.label.state": "收件字段 · 邦 / 省",
  "shipping.label.city": "收件字段 · 城市",
  "shipping.label.postal_code": "收件字段 · PIN 码",
  "shipping.label.address_line1": "收件字段 · 详细地址 1",
  "shipping.label.address_line2": "收件字段 · 详细地址 2",
  "shipping.label.note": "收件字段 · 备注",
  "shipping.button.confirm": "收件 · 确认按钮",
  "shipping.button.save": "收件 · 保存按钮",
  "shipping.button.saving": "收件 · 保存中按钮",
  "shipping.button.edit": "收件 · 修改按钮",
  "shipping.button.use_existing": "收件 · 使用该地址按钮",
  "shipping.success.saved": "收件 · 地址保存成功提示",
  "shipping.success.confirmed": "收件 · 地址锁定成功提示",
  "shipping.error.required": "收件 · 必填项错误",
  "shipping.error.phone_invalid": "收件 · 手机格式错误",
  "shipping.error.pin_invalid": "收件 · PIN 码错误",
  "shipping.error.not_required": "收件 · 无需邮寄错误",
  "shipping.error.already_confirmed": "收件 · 地址已锁定错误",

  "share.button": "分享 · 按钮",
  "share.text": "分享 · 中奖分享文案",

  "broadcast.jackpot": "大奖广播 · 频道播报文案",
  "push.deposit_credited": "推送 · 充值到账通知",
  "prize.wg_pay.succeeded": "WG 派彩 · 到账成功通知",
  "prize.wg_pay.pending": "WG 派彩 · 排队中通知",
  "prize.wg_pay.failed": "WG 派彩 · 失败通知",
};

/** 取文案 key 的中文描述；未收录时回退 key 本身。 */
export const textKeyZh = (key: string): string => TEXT_KEY_ZH[key] ?? key;
